import React, { useContext, useEffect, useState } from 'react';
import { HeadlineWithBreadcrumbNavigation } from '../../../../common/components/BreadcrumbNavigation';
import { ContentCloudComponentProps, withContentCloud } from '../WithContentCloud';
import { AppContext } from '../../../../common/contexts/AppContext';
import { ContentCloudPermission, ContentCloudService } from '../shared-permissions';
import { ContentBox } from '../../../Shared/ContentBox';
import { CopyToClipboardButton, Container } from '@edgebox/react-components';
import { ApiContext } from '../../../../common';
import { CONTENT_CLOUD_API_VERSION, getContentCloudSatelliteUrl } from '../content-cloud-helper';
import { useLocation } from 'react-router';
import { ensureProjectOfType } from '../../../RouterHelper';
import { SiteApplicationType } from '@edgebox/sync-core-data-definitions';

interface Props extends ContentCloudComponentProps {}

function ClientGenerationPage({ contentCloudData }: Props) {
  const api = useContext(ApiContext);
  const appContext = useContext(AppContext);
  const { project } = appContext;

  const location = useLocation();

  const { contentCloud, environment, space } = contentCloudData ?? {};

  const [devAccessToken, setDevAccessToken] = useState<string | null>(null);

  useEffect(() => {
    if (!api || !contentCloud || !project) {
      return;
    }

    api.syndication.remoteServices
      .getContentCloudRequestToken(contentCloud.id, project.id, {
        permissions: [ContentCloudPermission.SPACE_READ, ContentCloudPermission.CONTENT_TYPE_READ, ContentCloudPermission.DEVELOPER],
        services: [ContentCloudService.PUBLISHER],
      })
      .then((response) => setDevAccessToken(response));
  }, [api, contentCloud, project]);

  return (
    <Container>
      <div>
        <HeadlineWithBreadcrumbNavigation>Automated client generation</HeadlineWithBreadcrumbNavigation>
        <ContentBox className="p-4">
          <div className="mb-5">
            <div className="small fw-bold border-bottom text-muted pb-1">Access token</div>
            <div className="pt-1">
              <CopyToClipboardButton name="copy-graphql-url-cdn" text={devAccessToken ?? 'loading...'} buttonText="" />
            </div>
            <div className="text-muted small">
              Provide this access token to the Content Cloud CLI to generate the client code for your space.
            </div>
          </div>

          <div className="mb-5">
            <div className="small fw-bold border-bottom text-muted pb-1">Generate schema</div>
            <div className="position-relative pt-3 ps-3 pe-2">
              <code className="p-2 d-block">
                <pre>
                  {devAccessToken && contentCloud
                    ? `
export CC_ACCESS_TOKEN="${devAccessToken}"
export CC_BASE_URL="${contentCloud.baseUrl}"

content-cloud generate rest typescript -o app/content-cloud
`
                    : 'Loading...'}
                </pre>
              </code>
            </div>
            <div className="text-muted small">
              This will generate the schema file for typescript and download the client code + authentication helper.
            </div>
          </div>

          <div className="mb-5">
            <div className="small fw-bold border-bottom text-muted pb-1">Use client</div>
            <div className="position-relative pt-3 ps-3 pe-2">
              <code className="p-2 d-block">
                <pre>
                  {devAccessToken && contentCloud
                    ? `
# To use the client, you need to set the base URL through config or as an environment variable like this:
export CC_SATELLITE_BASE_URL="${getContentCloudSatelliteUrl(contentCloud, { api: 'rest', service: 'live', environmentSubdomain: `${space?.domainKey}-${environment?.domainKey}`, version: CONTENT_CLOUD_API_VERSION })}"

# To use the authentication helper, you'll need to set these additional environment variables *in your backend only*
# *never* share the client secret with your users and *never* add it to client code.
export CC_SPACE_ID="${space?.id}"
export CC_ENVIRONMENT_ID="${environment?.id}"

# To authenticate your application, you need to create a client with a secret and provide the ID + secret to your app.
# This secret will be used to authorize your requests by signing JWTs.
# You can generate a client here: https://${typeof window !== 'undefined' ? window.location.hostname : ''}/projects/${project?.id}/settings/clients
# e.g. "051aB000AAAAAAAAAAAAAA:abcdef:ghijKL+CS/="
export CC_CLIENT_SECRET="<CLIENT_ID>:<CLIENT_ISSUER_URL_BASE64>:<CLIENT_SECRET_BASE64>"

# e.g. in your Next.js project:
# npm run dev
`
                    : 'Loading...'}
                </pre>
              </code>
            </div>
            <div className="text-muted small">
              Add the environment variables before starting your dev environment to provide access to these settings to your client. With
              these settings provided, you can simply instantiate your <em>new ContentCloudRestClient()</em> and start using it.
            </div>
          </div>
        </ContentBox>
      </div>
    </Container>
  );
}

export default ensureProjectOfType(
  withContentCloud(ClientGenerationPage, [
    ContentCloudPermission.SPACE_READ,
    ContentCloudPermission.CONTENT_TYPE_READ,
    ContentCloudPermission.DEVELOPER,
  ]),
  SiteApplicationType.ContentCloud
);
